import React, { useMemo } from 'react';
import { useEcoLayoutTuning } from '../../contexts/EcoLayoutTuningContext';
import {
  MEASURES_PER_TRAIN_ROW,
  wagonRefForCellIndex,
  type EcoRitmoExercise,
  type WagonRef,
} from '../../lib/rhythm/ecoRitmoConfig';
import { trainSpriteForSignature } from '../../lib/rhythm/trainSprites';
import { EcoTrainSpriteMeasure } from './EcoTrainSpriteMeasure';
import type { FigureCellState } from './FigureGrid';

interface EcoTrainPlayViewProps {
  exercise: EcoRitmoExercise;
  activeCellIndex: number;
  cellStates?: FigureCellState[];
}

export const EcoTrainPlayView: React.FC<EcoTrainPlayViewProps> = ({
  exercise,
  activeCellIndex,
  cellStates,
}) => {
  const { cssVarsFor } = useEcoLayoutTuning();
  const rows = Math.ceil(exercise.measureCount / MEASURES_PER_TRAIN_ROW);
  const sprite = trainSpriteForSignature(exercise.timeSignature);

  const layoutCssVars = useMemo(
    () => cssVarsFor(exercise.timeSignature, exercise.measureCount),
    [cssVarsFor, exercise.timeSignature, exercise.measureCount]
  );

  const activeWagon = useMemo(
    () =>
      activeCellIndex >= 0
        ? wagonRefForCellIndex(exercise.pattern, activeCellIndex)
        : null,
    [exercise.pattern, activeCellIndex]
  );

  const config = useMemo(
    () => ({
      measureCount: exercise.measureCount,
      timeSignature: exercise.timeSignature,
      bpm: exercise.bpm,
      soundId: exercise.soundId,
      pattern: exercise.pattern,
    }),
    [exercise]
  );

  const isActiveWagon = (ref: WagonRef) =>
    activeWagon != null &&
    activeWagon.measureIndex === ref.measureIndex &&
    activeWagon.slotIndex === ref.slotIndex;

  const slotPlayState = (measureIndex: number, slotIndex: number): FigureCellState => {
    const fig = exercise.pattern[measureIndex]?.[slotIndex];
    if (!fig) return 'idle';
    let cellIdx = 0;
    for (let mi = 0; mi < exercise.pattern.length; mi++) {
      const slots = exercise.pattern[mi] ?? [];
      for (let si = 0; si < slots.length; si++) {
        if (slots[si] != null) {
          if (mi === measureIndex && si === slotIndex) {
            if (fig.startsWith('silencio')) return cellStates?.[cellIdx] ?? 'rest';
            return (
              cellStates?.[cellIdx] ??
              (cellIdx === activeCellIndex ? 'active' : 'idle')
            );
          }
          cellIdx++;
        }
      }
    }
    return 'idle';
  };

  if (!sprite) {
    return (
      <p className="text-sm font-bold text-center" style={{ color: '#1e4976' }}>
        Patrón listo para tocar
      </p>
    );
  }

  return (
    <div
      className="eco-train-play flex-1 min-h-0 w-full flex flex-col min-w-0"
      style={layoutCssVars as React.CSSProperties}
    >
      <div className="eco-train-stage flex-1 min-h-0 flex flex-col items-stretch eco-train-stage--sprites eco-train-stage--play px-1">
        {Array.from({ length: rows }).map((_, row) => {
          const start = row * MEASURES_PER_TRAIN_ROW;
          const end = Math.min(start + MEASURES_PER_TRAIN_ROW, exercise.measureCount);
          return (
            <div key={row} className="eco-train-row w-full flex flex-col items-center min-h-0">
              <div className={`eco-train-row-sprites eco-train-row-sprites--${end - start}`}>
                {Array.from({ length: end - start }).map((_, i) => {
                  const measureIndex = start + i;
                  return (
                    <EcoTrainSpriteMeasure
                      key={measureIndex}
                      measureIndex={measureIndex}
                      config={config}
                      sprite={sprite}
                      mode="play"
                      isSlotActive={isActiveWagon}
                      slotPlayState={slotPlayState}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EcoTrainPlayView;
